// The agent's free invitation to a past client (card 3841). Kept apart from
// the property's enrollment status, so it shows as its own small badge.
export const INVITE_LABELS = {
  sent: "Invitation sent",
  declined: "Invitation declined",
  viewed: "Risk assessment viewed",
  purchased: "Protection purchased",
};

export const INVITE_BADGE_CLASS = {
  sent: "bg-sky-50 text-sky-700",
  declined: "bg-rose-50 text-rose-700",
  viewed: "bg-indigo-50 text-indigo-700",
  purchased: "bg-emerald-50 text-emerald-700",
};

export const inviteLabel = (invite) => INVITE_LABELS[invite?.status] || "";

// The wizard shows Invite or Purchase only for Past Client → Standard Signup.
export const isCarPastClientStandardSignup = ({
  isServiceProvider,
  createMode,
  carPortfolioPrefill,
}) =>
  Boolean(isServiceProvider) &&
  createMode === "standard" &&
  carPortfolioPrefill?.enrollment_kind === "post_close";

export const sendInviteUrl = (portfolioId) =>
  `service-provider/car-portfolio/${portfolioId}/send-invite`;

export const declineInviteUrl = (token) =>
  `car-invite/${encodeURIComponent(token)}/decline`;

export const openInviteUrl = (token) =>
  `car-invite/${encodeURIComponent(token)}/open`;

export const OWNER_INVITES_URL = "car-owner-invites";

// SetupAccount shows the invitation heading when it sees this intent.
export const CAR_RISK_SETUP_INTENT = "car-risk";

export const setupAccountPathForInvite = (setupToken) =>
  `/setup-account?token=${encodeURIComponent(setupToken)}&intent=${CAR_RISK_SETUP_INTENT}`;

export const isOpenInvite = (invite) =>
  ["sent", "viewed"].includes(invite?.status);

// An invited property is bought through the agent's CAR plans, not the
// normal Add Subscription checkout. Invitations point at Complete Protection
// properties (the properties table); a Basic/Enhanced item on My Properties
// comes from property_risk_measure and its id is from that table, so it never
// matches.
export const openInviteForProperty = (invites, property) => {
  if (!property || property.from_risk_measure) return null;
  return (
    (Array.isArray(invites) ? invites : []).find(
      (invite) =>
        isOpenInvite(invite) &&
        invite?.property_id != null &&
        Number(invite.property_id) === Number(property.id),
    ) || null
  );
};

// The invitations one checkout pays for: the clicked one first, then the
// same agent's other open ones.
export const checkoutInvites = (invites, inviteId) => {
  const list = Array.isArray(invites) ? invites : [];
  const clicked = list.find((invite) => Number(invite.id) === Number(inviteId));
  if (!clicked) return [];
  const sameAgent = (invite) =>
    String(invite?.agent_id ?? "") === String(clicked.agent_id ?? "");
  return [
    clicked,
    ...list.filter(
      (invite) =>
        invite !== clicked && sameAgent(invite) && isOpenInvite(invite),
    ),
  ];
};

export const inviteCheckoutPath = (inviteId, plan) =>
  `/car-invite/checkout?invite=${encodeURIComponent(inviteId)}&plan=${encodeURIComponent(plan)}`;

// The homeowner's own checkout: the agent's CAR terms at list price.
export const CAR_PLAN_IDS = ["car_1yr", "car_3yr", "car_5yr"];

export const inviteQuoteUrl = (inviteId) =>
  `car-owner-invites/${encodeURIComponent(inviteId)}/quote`;

export const invitePaymentUrl = (inviteId) =>
  `car-owner-invites/${encodeURIComponent(inviteId)}/payment`;

// The CAR term replaces the price of the Yearly plan, as in the agent's checkout.
// get-plans returns Stripe products: `id` is Stripe's, `plan_id` is ours.
export const yearlyPlanId = (plans) =>
  (Array.isArray(plans) ? plans : []).find((plan) => plan?.name === "Yearly")
    ?.plan_id ?? null;

// complete-payment after the bank's 3D Secure check.
export const completeInvitePaymentBody = (payment, userId, paid) => ({
  json_res_ids: payment?.json_res_ids,
  isPaymnetDone: paid,
  user_id: userId,
  is_renew: false,
});
